import type { ApiClient } from './client';
import type {
  AdCreative,
  FeaturedBuild,
  HomeTestimonial,
  ProductUpdate,
} from '../../components/home/homeTypes';

export interface AdminFeaturedBuild extends FeaturedBuild {
  id: number;
  status?: 'scheduled' | 'active' | 'archived' | string;
  sort?: number;
}

export interface AdminAdvertiser {
  id: number;
  name: string;
  is_active?: boolean;
  created_at?: string;
}

export interface AdminCampaign {
  id: number;
  advertiser_id: number;
  advertiser_name?: string;
  name: string;
  status?: 'draft' | 'active' | 'paused' | 'ended' | string;
  starts_at?: string | null;
  ends_at?: string | null;
  placement?: string;
  impressions?: number;
  clicks?: number;
}

export interface AdminCreative extends AdCreative {
  id?: number;
  status?: 'active' | 'paused' | 'archived' | string;
  advertiser_id?: number;
}

export interface SponsorshipReport {
  impressions?: number;
  clicks?: number;
  campaigns?: Array<{
    id: number;
    name: string;
    impressions: number;
    clicks: number;
  }>;
}

export const sponsorshipMethods = {
  // —— Featured builds (admin) ——
  async adminListFeaturedBuilds(): Promise<AdminFeaturedBuild[]> {
    const response = await this.makeRequest<AdminFeaturedBuild[]>(
      this.getAdminEndpoint('/admin/featured_builds.php')
    );
    if (!response.success) throw new Error(response.message || 'Failed to list featured builds');
    return response.data || [];
  },

  async adminCreateFeaturedBuild(payload: {
    vehicle_id: number;
    blurb?: string;
    headline?: string;
    summary?: string;
    starts_at?: string | null;
    ends_at?: string | null;
    is_active?: boolean;
  }): Promise<AdminFeaturedBuild> {
    const status = payload.is_active === false ? 'draft' : 'active';
    const response = await this.makeRequest<AdminFeaturedBuild>(
      this.getAdminEndpoint('/admin/featured_builds.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'create',
          vehicle_id: payload.vehicle_id,
          headline: payload.headline || payload.blurb || undefined,
          summary: payload.summary || payload.blurb || undefined,
          starts_at: payload.starts_at ?? null,
          ends_at: payload.ends_at ?? null,
          status,
        }),
      }
    );
    if (!response.success) throw new Error(response.message || response.error || 'Failed to create featured build');
    return response.data as AdminFeaturedBuild;
  },

  async adminUpdateFeaturedBuild(
    id: number,
    payload: Partial<{
      blurb: string;
      headline: string;
      summary: string;
      starts_at: string | null;
      ends_at: string | null;
      is_active: boolean;
      status: string;
      sort: number;
      priority: number;
    }>
  ): Promise<AdminFeaturedBuild> {
    const status =
      payload.status ||
      (payload.is_active === true ? 'active' : payload.is_active === false ? 'draft' : undefined);
    const response = await this.makeRequest<AdminFeaturedBuild>(
      this.getAdminEndpoint('/admin/featured_builds.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: status && Object.keys(payload).length <= 2 ? 'status' : 'update',
          id,
          headline: payload.headline || payload.blurb,
          summary: payload.summary || payload.blurb,
          starts_at: payload.starts_at,
          ends_at: payload.ends_at,
          priority: payload.priority ?? payload.sort,
          status,
        }),
      }
    );
    if (!response.success) throw new Error(response.message || response.error || 'Failed to update featured build');
    return response.data as AdminFeaturedBuild;
  },

  async adminArchiveFeaturedBuild(id: number): Promise<void> {
    const response = await this.makeRequest(this.getAdminEndpoint('/admin/featured_builds.php'), {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'archive', id }),
    });
    if (!response.success) throw new Error(response.message || response.error || 'Failed to archive featured build');
  },

  // —— Sponsorship (admin) ——
  async adminListAdvertisers(): Promise<AdminAdvertiser[]> {
    const response = await this.makeRequest<AdminAdvertiser[]>(
      this.getAdminEndpoint('/admin/sponsorship.php?resource=advertisers')
    );
    if (!response.success) throw new Error(response.message || 'Failed to list advertisers');
    return response.data || [];
  },

  async adminCreateAdvertiser(name: string): Promise<AdminAdvertiser> {
    const response = await this.makeRequest<AdminAdvertiser>(
      this.getAdminEndpoint('/admin/sponsorship.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ resource: 'advertiser', name }),
      }
    );
    if (!response.success) throw new Error(response.message || 'Failed to create advertiser');
    return response.data as AdminAdvertiser;
  },

  async adminListCampaigns(): Promise<AdminCampaign[]> {
    const response = await this.makeRequest<AdminCampaign[]>(
      this.getAdminEndpoint('/admin/sponsorship.php?resource=campaigns')
    );
    if (!response.success) throw new Error(response.message || 'Failed to list campaigns');
    return response.data || [];
  },

  async adminCreateCampaign(payload: {
    advertiser_id: number;
    name: string;
    placement?: string;
    starts_at?: string | null;
    ends_at?: string | null;
    status?: string;
  }): Promise<AdminCampaign> {
    const response = await this.makeRequest<AdminCampaign>(
      this.getAdminEndpoint('/admin/sponsorship.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ resource: 'campaign', ...payload }),
      }
    );
    if (!response.success) throw new Error(response.message || 'Failed to create campaign');
    return response.data as AdminCampaign;
  },

  async adminUpdateCampaign(
    id: number,
    payload: Partial<{ name: string; status: string; starts_at: string | null; ends_at: string | null; placement: string }>
  ): Promise<AdminCampaign> {
    const response = await this.makeRequest<AdminCampaign>(
      this.getAdminEndpoint('/admin/sponsorship.php'),
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ resource: 'campaign', id, ...payload }),
      }
    );
    if (!response.success) throw new Error(response.message || 'Failed to update campaign');
    return response.data as AdminCampaign;
  },

  async adminListCreatives(campaignId?: number): Promise<AdminCreative[]> {
    const q = campaignId ? `&campaign_id=${campaignId}` : '';
    const response = await this.makeRequest<AdminCreative[]>(
      this.getAdminEndpoint(`/admin/sponsorship.php?resource=creatives${q}`)
    );
    if (!response.success) throw new Error(response.message || 'Failed to list creatives');
    return response.data || [];
  },

  async adminCreateCreative(payload: FormData | Record<string, unknown>): Promise<AdminCreative> {
    const isForm = typeof FormData !== 'undefined' && payload instanceof FormData;
    if (isForm && !(payload as FormData).has('resource')) {
      (payload as FormData).append('resource', 'creative');
    }
    const body = isForm
      ? (payload as FormData)
      : JSON.stringify({ resource: 'creative', ...(payload as object) });
    const response = await this.makeRequest<AdminCreative>(
      this.getAdminEndpoint('/admin/sponsorship.php'),
      {
        method: 'POST',
        headers: isForm ? undefined : this.getAuthHeaders(),
        body,
      }
    );
    if (!response.success) throw new Error(response.message || 'Failed to create creative');
    return response.data as AdminCreative;
  },

  async adminUpdateCreative(
    id: number,
    payload: Partial<{ status: string; headline: string; body: string; cta_label: string }>
  ): Promise<AdminCreative> {
    const response = await this.makeRequest<AdminCreative>(
      this.getAdminEndpoint('/admin/sponsorship.php'),
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ resource: 'creative', id, ...payload }),
      }
    );
    if (!response.success) throw new Error(response.message || 'Failed to update creative');
    return response.data as AdminCreative;
  },

  async adminSponsorshipReport(): Promise<SponsorshipReport> {
    const response = await this.makeRequest<SponsorshipReport>(
      this.getAdminEndpoint('/admin/sponsorship.php?resource=report')
    );
    if (!response.success) throw new Error(response.message || 'Failed to load sponsorship report');
    return response.data || {};
  },

  // —— Product updates (admin) ——
  async adminListProductUpdates(): Promise<ProductUpdate[]> {
    const response = await this.makeRequest<ProductUpdate[]>(
      this.getAdminEndpoint('/admin/product_updates.php')
    );
    if (!response.success) throw new Error(response.message || 'Failed to list product updates');
    return response.data || [];
  },

  async adminCreateProductUpdate(payload: {
    title: string;
    body?: string;
    summary?: string;
    href?: string;
    link_url?: string;
    published_at?: string | null;
    audience?: string;
    status?: string;
  }): Promise<ProductUpdate> {
    const response = await this.makeRequest<ProductUpdate>(
      this.getAdminEndpoint('/admin/product_updates.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'create',
          title: payload.title,
          summary: payload.summary || payload.body || '',
          link_url: payload.link_url || payload.href || null,
          // Omit starts_at for immediate publish. Sending browser ISO/Z can store a
          // future wall-clock in MySQL and hide the message from the home feed.
          starts_at: payload.published_at ? payload.published_at : null,
          audience: payload.audience || 'all',
          status: payload.status || 'active',
        }),
      }
    );
    if (!response.success) throw new Error(response.message || response.error || 'Failed to create product update');
    return response.data as ProductUpdate;
  },

  async adminUpdateProductUpdate(
    id: number,
    payload: Partial<{
      title: string;
      body: string;
      summary: string;
      href: string;
      link_url: string;
      published_at: string | null;
      status: string;
      audience: string;
    }>
  ): Promise<ProductUpdate> {
    const response = await this.makeRequest<ProductUpdate>(
      this.getAdminEndpoint('/admin/product_updates.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'update',
          id,
          title: payload.title,
          summary: payload.summary || payload.body,
          link_url: payload.link_url || payload.href,
          starts_at: payload.published_at,
          status: payload.status,
          audience: payload.audience,
        }),
      }
    );
    if (!response.success) throw new Error(response.message || response.error || 'Failed to update product update');
    return response.data as ProductUpdate;
  },

  async adminDeleteProductUpdate(id: number): Promise<void> {
    const response = await this.makeRequest(this.getAdminEndpoint('/admin/product_updates.php'), {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'delete', id }),
    });
    if (!response.success) throw new Error(response.message || response.error || 'Failed to delete product update');
  },

  // —— Homepage testimonials (admin) ——
  async adminListTestimonials(status: string = 'all'): Promise<HomeTestimonial[]> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await this.makeRequest<HomeTestimonial[]>(
      this.getAdminEndpoint(`/admin/testimonials.php${qs}`)
    );
    if (!response.success) throw new Error(response.message || 'Failed to list testimonials');
    return response.data || [];
  },

  async adminCreateTestimonial(payload: {
    quote: string;
    author_name: string;
    author_handle?: string | null;
    vehicle_label?: string | null;
    rating?: number;
    avatar_url?: string | null;
    sort_order?: number;
    status?: string;
    is_placeholder?: boolean;
    source?: string;
  }): Promise<HomeTestimonial> {
    const response = await this.makeRequest<HomeTestimonial>(
      this.getAdminEndpoint('/admin/testimonials.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'create', ...payload }),
      }
    );
    if (!response.success) throw new Error(response.message || response.error || 'Failed to create testimonial');
    return response.data as HomeTestimonial;
  },

  async adminUpdateTestimonial(
    id: number,
    payload: Partial<{
      quote: string;
      author_name: string;
      author_handle: string | null;
      vehicle_label: string | null;
      rating: number;
      avatar_url: string | null;
      sort_order: number;
      status: string;
      is_placeholder: boolean;
      source: string;
    }>
  ): Promise<HomeTestimonial> {
    const response = await this.makeRequest<HomeTestimonial>(
      this.getAdminEndpoint('/admin/testimonials.php'),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'update', id, ...payload }),
      }
    );
    if (!response.success) throw new Error(response.message || response.error || 'Failed to update testimonial');
    return response.data as HomeTestimonial;
  },

  async adminArchiveTestimonial(id: number): Promise<void> {
    const response = await this.makeRequest(this.getAdminEndpoint('/admin/testimonials.php'), {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'archive', id }),
    });
    if (!response.success) throw new Error(response.message || response.error || 'Failed to archive testimonial');
  },
} satisfies ThisType<ApiClient>;
